import { DATA_PHONE_COUNTRY_CODES } from "../data/phoneCountryCodes";
import { readAddressTypes } from "./addressTypes";
import { canonicalCountryName, nationalityCountry } from "./nationalityCountry";
import { isPhoneComplete } from "./phoneUtils";

const MOBILE_PHONE_TYPES = new Set(["mobile", "pager"]);

const isCorporate = (values) =>
  String(values?.type || "").trim().toUpperCase() === "CORPORATE";

const addCheck = (checks, country, source) => {
  const name = canonicalCountryName(country);
  if (!name) return;
  checks.push({ source, country: name });
};

const groupChecks = (checks = []) => {
  const countries = [];
  checks.forEach((check) => {
    const key = check.country.toLowerCase();
    const existing = countries.find((item) => item.country.toLowerCase() === key);
    if (existing) {
      if (!existing.sources.includes(check.source)) existing.sources.push(check.source);
      return;
    }
    countries.push({ country: check.country, sources: [check.source] });
  });
  return countries;
};

const countriesFromDialCode = (code) => {
  const digits = String(code ?? "").replace(/\D/g, "");
  if (!digits) return [];
  const match = DATA_PHONE_COUNTRY_CODES.find((item) => item.value === digits);
  if (!match?.country) return [];
  return String(match.country)
    .split("/")
    .map((part) => canonicalCountryName(part))
    .filter(Boolean);
};

const addPhoneChecks = (checks, values) => {
  const phones = Array.isArray(values.phones) ? values.phones : [];
  phones.forEach((phone) => {
    const type = String(phone?.type || "mobile").trim().toLowerCase();
    if (!MOBILE_PHONE_TYPES.has(type) || !isPhoneComplete(phone)) return;
    countriesFromDialCode(phone.countryCode).forEach((country) => {
      addCheck(checks, country, "Mobile country code");
    });
  });
};

const addAddressChecks = (checks, values, { corporate }) => {
  const addresses = Array.isArray(values.addresses) ? values.addresses : [];
  addresses.forEach((address) => {
    const country = address?.country;
    if (!String(country || "").trim()) return;
    const types = readAddressTypes(address);
    if (!corporate && types.includes("residental")) {
      addCheck(checks, country, "Residential Address");
    }
    if (types.includes("correspondence")) {
      addCheck(checks, country, "Correspondence Address");
    }
    if (corporate && types.includes("registeredOffice")) {
      addCheck(checks, country, "Business Address");
      addCheck(checks, country, "Operating Address");
    }
  });
};

export const resolveClientDueDiligenceChecks = (values = {}) => {
  const checks = [];
  const corporate = isCorporate(values);

  if (corporate) {
    addCheck(checks, values.placeOfIncorporation, "Place of Incorporation");
    addPhoneChecks(checks, values);
    addAddressChecks(checks, values, { corporate: true });
    return checks;
  }

  addCheck(checks, nationalityCountry(values.nationality), "Nationality");
  addPhoneChecks(checks, values);
  addCheck(checks, values.placeOfBirth, "Place of Birth");
  addCheck(checks, values.residentialCountry, "Residential Country");
  addAddressChecks(checks, values, { corporate: false });
  return checks;
};

export const resolveClientDueDiligenceCountries = (values = {}) =>
  groupChecks(resolveClientDueDiligenceChecks(values));

export const dueDiligenceCountryValue = (countries = []) =>
  countries
    .map((item) => String(item?.country || "").trim())
    .filter(Boolean)
    .join(", ");

export const resolveClientDueDiligenceCountry = (values = {}) => {
  const [first] = resolveClientDueDiligenceCountries(values);
  if (!first) return { country: "", source: null };
  return { country: first.country, source: first.sources[0] || null };
};

export const assignDueDiligenceCountry = (values = {}, country = "") => ({
  ...values,
  dueDiligence: {
    ...(values.dueDiligence || {}),
    country: country || "",
  },
});
